import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface ProfileSectionProps {
  onBack: () => void;
}

function loginMethodLabel(provider: 'google' | 'apple' | 'email' | null): string {
  if (provider === 'google') return 'Google';
  if (provider === 'apple') return 'Apple';
  if (provider === 'email') return 'Email & password';
  return '—';
}

export function ProfileSection({ onBack }: ProfileSectionProps) {
  const { profile, updateProfile } = useProfile();
  const { user, loginProvider } = useAuth();
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [isSaving, setIsSaving] = useState(false);

  // Keep display name in sync when profile loads or updates (e.g. from auth provider)
  useEffect(() => {
    const name = profile?.display_name ?? '';
    setDisplayName((prev) => (prev === name ? prev : name));
  }, [profile?.display_name]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await updateProfile({ display_name: displayName || null });
      if (error) throw error;
      
      toast({
        title: 'Profile updated',
        description: 'Your changes have been saved.',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Personal Info</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
            />
            <p className="text-sm text-muted-foreground">
              This is how you'll appear in the app
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={user?.email || ''}
              disabled
              className="bg-muted"
            />
            <p className="text-sm text-muted-foreground">
              Your email address is managed through security settings
            </p>
          </div>

          <div className="space-y-2">
            <Label>Sign in with</Label>
            <p className="text-sm text-muted-foreground">
              {loginMethodLabel(loginProvider)}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t">
        <Button
          onClick={handleSave}
          className="w-full"
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );
}
